import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Star, Clock, XCircle, CheckCircle, Warning, Quotes, Tag, ShoppingCart, LinkSimple, SealCheck,
} from "@phosphor-icons/react";
import { Tip, buildVerdict, evidenceLabel, platformName } from "./commerceUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REJECT_REASONS = [
  { id: "already_saturated", label: "Already saturated" },
  { id: "not_productizable", label: "Not productizable" },
  { id: "medical_issue", label: "Medical issue" },
  { id: "too_generic", label: "Too generic" },
  { id: "poor_clustering", label: "Poor clustering" },
  { id: "duplicate_cluster", label: "Duplicate cluster" },
  { id: "not_enough_evidence", label: "Not enough evidence" },
  { id: "other", label: "Other" },
];

const STATUS_META = {
  approved: { label: "Build Candidate", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: CheckCircle },
  approved_for_research: { label: "Research Further", cls: "bg-violet-50 text-violet-700 ring-violet-200", Icon: CheckCircle },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 ring-rose-200", Icon: XCircle },
  review_later: { label: "Needs Investigation", cls: "bg-amber-50 text-amber-700 ring-amber-200", Icon: Clock },
  pending: { label: "Pending", cls: "bg-zinc-100 text-zinc-500 ring-zinc-200", Icon: Warning },
};

const authHeader = () => {
  const t = localStorage.getItem("p2p_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const StatChip = ({ label, value, tone }) => (
  <div className={`rounded-xl border px-3.5 py-3 shadow-sm ${tone}`} data-testid={`review-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-2xl font-black leading-none">{value}</div>
    <div className="text-[10px] mt-1 font-semibold uppercase tracking-wide opacity-70">{label}</div>
  </div>
);

const MixLine = ({ label, dist }) => {
  const total = Object.values(dist || {}).reduce((a, b) => a + b, 0) || 1;
  const high = dist?.high || 0;
  return (
    <span className="text-xs text-zinc-600">
      {label}: <span className="font-bold text-zinc-900">{Math.round((high / total) * 100)}% high</span>
    </span>
  );
};

const AI_REC_META = {
  Approve: "bg-emerald-600 text-white",
  "Review Later": "bg-amber-500 text-white",
  Reject: "bg-rose-600 text-white",
};

const rating3 = (v) => (v === "High" ? "text-emerald-600" : v === "Medium" ? "text-amber-600" : "text-zinc-500");
const satTone = (v) => (v === "Low" ? "text-emerald-600" : v === "Medium" ? "text-amber-600" : "text-rose-600");

const AiAnalystPanel = ({ a }) => (
  <div className="mt-4 rounded-xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 p-4" data-testid="ai-analyst-panel">
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2 text-violet-700 font-bold text-xs uppercase tracking-wide">
        <Star size={13} weight="fill" /> AI Business Assessment
        <Tip text="An AI analyst's read on whether this problem is a real product business. Advisory only — you decide." />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">Business Potential</span>
        <span className="text-lg font-black text-zinc-900 leading-none">{a.commercial_viability_score} / 100</span>
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${AI_REC_META[a.ai_recommendation] || "bg-zinc-200"}`} data-testid="ai-recommendation">
          {a.ai_recommendation}
        </span>
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-[11px]">
      <div>Can a Product Solve This?<Tip text="Whether a physical product could realistically fix this problem." /> <span className="font-bold text-zinc-800">{a.productizable}</span></div>
      <div>Competition Level<Tip text="How crowded the market already is with existing products." /> <span className={`font-bold ${satTone(a.market_saturation)}`}>{a.market_saturation}</span></div>
      <div>Existing Competition<Tip text="How many rivals already serve this demand." /> <span className={`font-bold ${satTone(a.existing_competition)}`}>{a.existing_competition}</span></div>
      <div>Difficulty to Launch<Tip text="How hard it is to build and bring this to market (1 easy – 10 hard)." /> <span className="font-bold text-zinc-800">{a.build_difficulty}/10</span></div>
      <div>Easy to Manufacture?<Tip text="How easily this could be produced as a private-label product." /> <span className={`font-bold ${rating3(a.private_label_potential)}`}>{a.private_label_potential}</span></div>
      <div>Easy to Stand Out?<Tip text="How easy it is to differentiate from existing products." /> <span className={`font-bold ${rating3(a.differentiation_potential)}`}>{a.differentiation_potential}</span></div>
      <div>Repeat Purchase Potential<Tip text="How likely customers are to buy again and again." /> <span className={`font-bold ${rating3(a.recurring_purchase_potential)}`}>{a.recurring_purchase_potential}</span></div>
      <div>Typical Selling Price<Tip text="Estimated retail price range for a solution." /> <span className="font-bold text-zinc-800">{a.retail_price_range || "—"}</span></div>
      <div className="col-span-2 sm:col-span-4">Profit Margin Potential<Tip text="Estimated gross profit margin on a solution." /> <span className="font-bold text-zinc-800">{a.gross_margin_potential || "—"}</span></div>
    </div>
    {a.ai_reasoning?.length > 0 && (
      <ul className="mt-3 space-y-1 border-t border-violet-100 pt-2.5">
        {a.ai_reasoning.map((b, i) => (
          <li key={i} className="text-[11px] text-zinc-600 flex gap-1.5">
            <span className="text-violet-400 flex-shrink-0">→</span>{b}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const VERDICT_TONE = {
  emerald: "from-emerald-50 border-emerald-200 text-emerald-700",
  amber: "from-amber-50 border-amber-200 text-amber-700",
  rose: "from-rose-50 border-rose-200 text-rose-700",
};

const FinalVerdict = ({ item }) => {
  const v = buildVerdict(item);
  return (
    <div className={`mt-4 rounded-xl bg-gradient-to-br to-white border p-4 ${VERDICT_TONE[v.tone]}`} data-testid={`review-verdict-${item.cluster_id}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide mb-1.5">
        <SealCheck size={14} weight="fill" /> Final Verdict
        <Tip text="Plain-English summary of demand, competition, margins and difficulty from the analysis above." />
      </div>
      <div className="text-base font-extrabold text-zinc-900">{v.label}</div>
      <ul className="mt-2 space-y-1">
        {v.bullets.map((b, i) => (
          <li key={i} className="text-[11px] text-zinc-700 flex gap-1.5"><span className="text-zinc-400 flex-shrink-0">•</span>{b}</li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] font-semibold text-zinc-900">{v.nextStep}</p>
    </div>
  );
};

const ReviewCard = ({ item, onSet, busy }) => {
  const [showReasons, setShowReasons] = useState(false);
  const meta = STATUS_META[item.approval_status] || STATUS_META.pending;

  return (
    <div className="rounded-2xl bg-white/85 backdrop-blur border border-violet-100 p-5 shadow-sm" data-testid={`review-card-${item.cluster_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{item.market}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${meta.cls}`} data-testid={`review-status-${item.cluster_id}`}>
              <meta.Icon size={11} weight="fill" /> {meta.label}
            </span>
            {item.approval_reason && (
              <span className="text-[10px] text-rose-500 font-medium">
                ({REJECT_REASONS.find((r) => r.id === item.approval_reason)?.label || item.approval_reason})
              </span>
            )}
          </div>
          <h3 className="mt-2 text-base font-bold text-zinc-900">{item.cluster_name}</h3>
        </div>
        <div className="flex gap-2 flex-shrink-0 text-center">
          <div>
            <div className="text-xl font-black text-zinc-900 leading-none">{item.baseline_score ?? "—"}</div>
            <div className="text-[9px] text-zinc-400 mt-0.5 flex items-center justify-center">Demand<Tip text="How strong demand looks from Google Trends + search interest." /></div>
          </div>
          <div>
            <div className="text-xl font-black text-violet-600 leading-none">{item.enriched_score ?? "—"}</div>
            <div className="text-[9px] text-zinc-400 mt-0.5 flex items-center justify-center">Verified<Tip text="Demand cross-checked with TikTok, Pinterest & YouTube signals." /></div>
          </div>
        </div>
      </div>

      {/* meta row */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        <span className="text-xs text-zinc-600">Customer Signals: <span className="font-bold text-zinc-900">{item.frequency}</span></span>
        <MixLine label="Purchase Intent" dist={item.buying_intent_dist} />
        <MixLine label="Problem Severity" dist={item.severity_dist} />
      </div>

      {/* keywords */}
      {item.keywords?.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {item.keywords.slice(0, 8).map((k) => (
            <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{k}</span>
          ))}
        </div>
      )}

      {/* quotes */}
      {item.sample_quotes?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {item.sample_quotes.slice(0, 2).map((q, i) => (
            <div key={i} className="text-xs text-zinc-600 italic flex gap-1.5">
              <Quotes size={13} className="text-violet-300 flex-shrink-0 mt-0.5" weight="fill" />
              <span>"{q.text}"</span>
            </div>
          ))}
        </div>
      )}

      {/* evidence links */}
      {(item.evidence?.length > 0 || item.source_urls?.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(item.evidence || []).slice(0, 4).map((e, i) => (
            <a key={`e${i}`} href={e.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-violet-600 hover:underline">
              <LinkSimple size={11} /> {platformName(e.source) || evidenceLabel(e.url)}
            </a>
          ))}
          {(item.source_urls || []).slice(0, 2).map((u, i) => (
            <a key={`s${i}`} href={u} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:underline">
              <LinkSimple size={11} /> {evidenceLabel(u)}
            </a>
          ))}
        </div>
      )}

      {/* AI Business Assessment (Phase 4.6) — advisory, human still decides */}
      {item.ai_analysis && <AiAnalystPanel a={item.ai_analysis} />}

      {/* Final Verdict — plain-English summary from stored outputs */}
      <FinalVerdict item={item} />

      {/* actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-3">
        {item.ai_analysis && (
          <span className="text-[10px] text-zinc-400 mr-1">AI suggests <b className="text-zinc-600">{item.ai_analysis.ai_recommendation}</b> — you decide:</span>
        )}
        <button disabled={busy} onClick={() => onSet(item.cluster_id, "approved")}
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full transition-all disabled:opacity-50 ${item.approval_status === "approved" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
          data-testid={`review-approve-${item.cluster_id}`}>
          <Star size={13} weight="fill" /> Build Candidate
        </button>
        <button disabled={busy} onClick={() => onSet(item.cluster_id, "approved_for_research")}
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full transition-all disabled:opacity-50 ${item.approval_status === "approved_for_research" ? "bg-violet-600 text-white" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}
          data-testid={`review-research-${item.cluster_id}`}>
          <Star size={13} weight="fill" /> Research Further
        </button>
        <button disabled={busy} onClick={() => onSet(item.cluster_id, "review_later")}
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full transition-all disabled:opacity-50 ${item.approval_status === "review_later" ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
          data-testid={`review-later-${item.cluster_id}`}>
          <Clock size={13} weight="fill" /> Needs Investigation
        </button>
        <button disabled={busy} onClick={() => setShowReasons((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full transition-all disabled:opacity-50 ${item.approval_status === "rejected" ? "bg-rose-600 text-white" : "bg-rose-50 text-rose-700 hover:bg-rose-100"}`}
          data-testid={`review-reject-${item.cluster_id}`}>
          <XCircle size={13} weight="fill" /> Reject
        </button>
      </div>
      {showReasons && (
        <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`review-reasons-${item.cluster_id}`}>
          {REJECT_REASONS.map((r) => (
            <button key={r.id} disabled={busy}
              onClick={() => { onSet(item.cluster_id, "rejected", r.id); setShowReasons(false); }}
              className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
              data-testid={`review-reason-${r.id}-${item.cluster_id}`}>
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QualityReview = () => {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios.get(`${API}/commerce/review/queue`).then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const setReview = async (cluster_id, status, reason) => {
    setBusy(true);
    setData((d) => ({ // optimistic
      ...d,
      items: d.items.map((it) => it.cluster_id === cluster_id
        ? { ...it, approval_status: status, approval_reason: status === "rejected" ? reason : null } : it),
    }));
    try {
      await axios.post(`${API}/commerce/review`, { cluster_id, status, reason }, { headers: authHeader() });
      await load();
    } catch (e) { /* revert via reload */ await load(); }
    setBusy(false);
  };

  const dash = data?.dashboard;
  const items = useMemo(() => {
    const list = data?.items || [];
    return statusFilter === "all" ? list : list.filter((i) => i.approval_status === statusFilter);
  }, [data, statusFilter]);

  if (!data) {
    return <div className="text-center py-16 text-zinc-400" data-testid="review-loading">Loading review queue…</div>;
  }

  const FILTERS = [
    { id: "all", label: "All" },
    { id: "pending", label: "Pending" },
    { id: "approved", label: "Build Candidate" },
    { id: "approved_for_research", label: "Research Further" },
    { id: "review_later", label: "Needs Investigation" },
    { id: "rejected", label: "Rejected" },
  ];

  return (
    <div data-testid="commerce-quality-review">
      {/* Dashboard */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="review-dashboard">
        <StatChip label="Total" value={dash.total} tone="bg-white/80 border-violet-100 text-zinc-900" />
        <StatChip label="Build Candidates" value={dash.approved} tone="bg-emerald-50 border-emerald-100 text-emerald-700" />
        <StatChip label="Research Further" value={dash.approved_for_research || 0} tone="bg-violet-50 border-violet-100 text-violet-700" />
        <StatChip label="Rejected" value={dash.rejected} tone="bg-rose-50 border-rose-100 text-rose-700" />
        <StatChip label="Pending" value={dash.pending + dash.review_later} tone="bg-amber-50 border-amber-100 text-amber-700" />
        <StatChip label="Approval Rate" value={`${dash.approval_rate}%`} tone="bg-white/80 border-violet-100 text-zinc-900" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="review-filters">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${statusFilter === f.id ? "bg-violet-600 text-white shadow-sm" : "bg-white text-zinc-600 border border-zinc-200 hover:border-violet-300"}`}
            data-testid={`review-filter-${f.id}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Queue */}
      <div className="space-y-4" data-testid="review-queue-list">
        {items.length === 0
          ? <div className="rounded-2xl border border-dashed border-violet-200 bg-white/60 p-8 text-center text-sm text-zinc-500" data-testid="review-empty">No clusters in this view.</div>
          : items.map((it) => <ReviewCard key={it.cluster_id} item={it} onSet={setReview} busy={busy} />)}
      </div>
    </div>
  );
};

export default QualityReview;
